
import { useState } from 'react';

export default function HomePage() {
  const [expandedCard, setExpandedCard] = useState<number>(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitStatus('submitting');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d69iequa728k8ctu2os0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', message: '' });
        setTimeout(() => setSubmitStatus('idle'), 3000);
      } else {
        setSubmitStatus('error');
        setTimeout(() => setSubmitStatus('idle'), 3000);
      }
    } catch (error) {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus('idle'), 3000);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-50/30 via-teal-50/20 to-indigo-50/30"></div>
        
        <div className="relative w-full max-w-[1400px] mx-auto px-8 py-20">
          <div className="absolute top-8 left-8">
            <img 
              src="https://public.readdy.ai/ai/img_res/8517af25-c4e1-4f6e-8c34-77770aec8f69.png" 
              alt="DEA SH.P.K. Logo" 
              className="h-12 w-auto object-contain"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mt-20">
            {/* Left Content */}
            <div className="space-y-8">
              <div className="space-y-4">
                <h1 className="text-7xl font-bold leading-tight text-gray-900">
                  Modern iOS<br />
                  <span className="text-6xl">Applications</span>
                </h1>
                <p className="text-xl text-gray-600 font-medium">VPN & Cleaner Solutions</p>
              </div>
              
              <p className="text-base text-gray-700 leading-relaxed max-w-md">
                DEA SH.P.K. is a team of experts developing modern applications for the iOS platform. Our products are designed to enhance user experience and improve the security of mobile devices.
              </p>
            </div>

            {/* Right Visual */}
            <div className="relative h-[600px] flex items-center justify-center">
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-100/40 via-purple-100/30 to-teal-100/40 rounded-3xl"></div>
              
              {/* Decorative Elements */}
              <div className="absolute top-20 right-20 w-32 h-32 bg-white/40 rounded-full blur-2xl"></div>
              <div className="absolute bottom-32 left-16 w-24 h-24 bg-purple-200/30 rounded-2xl rotate-12"></div>
              
              {/* Main Device Mockup */}
              <div className="relative z-10 transform rotate-6 hover:rotate-3 transition-transform duration-500">
                <div className="w-72 h-[580px] bg-gray-900 rounded-[3rem] p-3 shadow-2xl">
                  <div className="w-full h-full bg-gradient-to-br from-indigo-500 to-purple-600 rounded-[2.5rem] overflow-hidden">
                    <img 
                      src="https://public.readdy.ai/ai/img_res/d674c6e24c2bc0a64347e070ae57c2c1.jpg" 
                      alt="VPN App Interface"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>

              {/* Secondary Device */}
              <div className="absolute bottom-8 right-8 z-20 transform -rotate-6 hover:-rotate-3 transition-transform duration-500">
                <div className="w-56 h-[450px] bg-gray-900 rounded-[2.5rem] p-2.5 shadow-xl">
                  <div className="w-full h-full bg-gradient-to-br from-teal-400 to-emerald-500 rounded-[2rem] overflow-hidden">
                    <img 
                      src="https://readdy.ai/api/search-image?query=iOS%20device%20cleaner%20application%20interface%20showing%20storage%20optimization%20with%20circular%20progress%20indicator%20and%20clean%20buttons%20in%20teal%20and%20green%20gradient%20with%20file%20categories%20and%20space%20saved%20statistics&width=224&height=450&seq=dea-cleaner-secondary&orientation=portrait" 
                      alt="Cleaner App Interface"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 bg-amber-50/30">
        <div className="max-w-[1200px] mx-auto px-8">
          <div className="mb-8">
            <span className="text-xs uppercase tracking-widest text-amber-800 font-medium">/expertise</span>
          </div>
          
          <div className="max-w-3xl ml-auto">
            <p className="text-3xl leading-relaxed text-gray-800 font-light">
              We are a dedicated team of iOS development specialists focused on creating applications that prioritize user security and device performance. Our expertise spans VPN technology and system optimization, delivering solutions that protect privacy while maintaining seamless functionality across all iOS devices.
            </p>
            
            <div className="mt-16 flex items-center gap-6">
              <div className="flex -space-x-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 border-2 border-white"></div>
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gray-400 to-gray-500 border-2 border-white"></div>
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gray-500 to-gray-600 border-2 border-white"></div>
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gray-600 to-gray-700 border-2 border-white"></div>
              </div>
              <span className="text-sm text-gray-700">Expert iOS Development Team</span>
            </div>
            
            <div className="mt-8">
              <span className="text-xs text-gray-500">· Based in Albania</span>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-8">
          <h2 className="text-6xl font-bold text-center text-gray-900 mb-20">Our Products 🚀</h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* VPN Card */}
            <div 
              className={`relative rounded-2xl overflow-hidden cursor-pointer transition-all duration-500 ${
                expandedCard === 0 ? 'lg:col-span-2' : 'lg:col-span-1'
              }`}
              style={{ aspectRatio: '4/5' }}
              onMouseEnter={() => setExpandedCard(0)}
            >
              <img 
                src="https://readdy.ai/api/search-image?query=abstract%20digital%20shield%20icon%20with%20blue%20and%20purple%20gradient%20background%20representing%20VPN%20security%20and%20encryption%20with%20network%20connection%20lines%20and%20lock%20symbols%20in%20modern%20minimalist%20style%20with%20soft%20lighting&width=800&height=1000&seq=dea-vpn-card&orientation=portrait" 
                alt="VPN Protection"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
              
              <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                <h3 className="text-3xl font-bold mb-4">VPN Protection</h3>
                <p className="text-base leading-relaxed opacity-90">
                  Secure browsing with military-grade encryption across all iOS devices. Protect your online privacy and access content safely from anywhere in the world.
                </p>
              </div>
            </div>

            {/* Cleaner Card */}
            <div 
              className={`relative rounded-2xl overflow-hidden cursor-pointer transition-all duration-500 ${
                expandedCard === 1 ? 'lg:col-span-2' : 'lg:col-span-1'
              }`}
              style={{ aspectRatio: '4/5' }}
              onMouseEnter={() => setExpandedCard(1)}
            >
              <img 
                src="https://readdy.ai/api/search-image?query=abstract%20visualization%20of%20storage%20optimization%20with%20teal%20and%20green%20gradient%20showing%20organized%20files%20and%20clean%20space%20with%20circular%20progress%20indicators%20and%20minimalist%20geometric%20shapes%20representing%20device%20cleaning%20and%20performance&width=800&height=1000&seq=dea-cleaner-card&orientation=portrait" 
                alt="Device Cleaner"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
              
              <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                <h3 className="text-3xl font-bold mb-4">Device Cleaner</h3>
                <p className="text-base leading-relaxed opacity-90">
                  Intelligent storage management removing junk files and optimizing performance. Keep your iOS device running smoothly with automated cleaning.
                </p>
              </div>
            </div>

            {/* Security Card */}
            <div 
              className={`relative rounded-2xl overflow-hidden cursor-pointer transition-all duration-500 ${
                expandedCard === 2 ? 'lg:col-span-2' : 'lg:col-span-1'
              }`}
              style={{ aspectRatio: '4/5' }}
              onMouseEnter={() => setExpandedCard(2)}
            >
              <img 
                src="https://readdy.ai/api/search-image?query=abstract%20lock%20and%20privacy%20themed%20visual%20with%20dark%20blue%20and%20indigo%20gradient%20showing%20security%20layers%20and%20encrypted%20data%20protection%20with%20fingerprint%20and%20shield%20elements%20in%20modern%20minimalist%20design%20style&width=800&height=1000&seq=dea-security-card&orientation=portrait" 
                alt="Privacy Shield"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
              
              <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                <h3 className="text-3xl font-bold mb-4">Privacy Shield</h3>
                <p className="text-base leading-relaxed opacity-90">
                  Advanced protection features safeguarding personal data and online activity. Comprehensive security tools designed for complete peace of mind.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-32 bg-white">
        <div className="max-w-[1200px] mx-auto px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
            {/* Left Column - Contact Form */}
            <div className="lg:col-span-2">
              <h2 className="text-4xl font-serif text-gray-900 mb-6 leading-tight">
                Stay Connected
              </h2>
              <p className="text-base text-gray-600 leading-relaxed mb-12 max-w-sm">
                Get updates about our latest iOS applications and security features
              </p>

              <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-xs uppercase tracking-wider text-gray-700 mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full bg-transparent border-b-2 border-gray-300 py-3 text-base text-gray-900 focus:border-gray-900 focus:outline-none transition-colors"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-xs uppercase tracking-wider text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="w-full bg-transparent border-b-2 border-gray-300 py-3 text-base text-gray-900 focus:border-gray-900 focus:outline-none transition-colors"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-xs uppercase tracking-wider text-gray-700 mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={(e) => {
                      if (e.target.value.length <= 500) {
                        setFormData({ ...formData, message: e.target.value });
                      }
                    }}
                    required
                    rows={4}
                    maxLength={500}
                    className="w-full bg-transparent border-b-2 border-gray-300 py-3 text-base text-gray-900 focus:border-gray-900 focus:outline-none transition-colors resize-none"
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500</p>
                </div>

                <button
                  type="submit"
                  disabled={submitStatus === 'submitting'}
                  className="w-full h-12 rounded-3xl border-2 border-gray-900 bg-transparent text-gray-900 font-medium hover:bg-gray-900 hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {submitStatus === 'submitting' ? 'Sending...' : submitStatus === 'success' ? 'Message Sent!' : submitStatus === 'error' ? 'Error. Try Again' : 'Send Message'}
                </button>
              </form>
            </div>

            {/* Right Column - Contact Info */}
            <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-3 gap-12">
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-6 uppercase tracking-wider">Products</h3>
                <ul className="space-y-4 text-sm text-gray-600">
                  <li>VPN Service</li>
                  <li>Device Cleaner</li>
                  <li>Privacy Tools</li>
                  <li>iOS Security</li>
                </ul>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-6 uppercase tracking-wider">Company</h3>
                <ul className="space-y-4 text-sm text-gray-600">
                  <li>About DEA SH.P.K.</li>
                  <li>Our Team</li>
                  <li>Technology</li>
                  <li>Careers</li>
                </ul>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-6 uppercase tracking-wider">Contact</h3>
                <div className="space-y-4 text-sm text-gray-600">
                  <div>
                    <p className="font-medium text-gray-700 mb-1">Address</p>
                    <p>LAGJJA BARRIKADA</p>
                    <p>RRUGA ANTIPATREA</p>
                    <p>GODINA E ISH-USHQIMORES</p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-700 mb-1">Location</p>
                    <p>BERAT, 5000</p>
                    <p>Albania</p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-700 mb-1">Email</p>
                    <p>help@deashpk.com</p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-700 mb-1">Phone</p>
                    <p>+1 9704788127</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Bottom */}
          <div className="mt-20 pt-8 border-t border-gray-200 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-500">© 2025 DEA SH.P.K. All rights reserved.</p>
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-sm text-gray-500 hover:text-gray-900 transition-colors cursor-pointer"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
